/* Draft presentation only: retain the review provider's data and click handlers. */
(() => {
  if (!window.customElements || customElements.get('fadlash-product-reviews')) return;

  const selectors = {
    expand: '#ddReview span[class*="CommonWidget_displayText__"]',
    write: '#ddReview div[class*="WriteReviewBtn_writeBtn__"]',
    sort: '#ddReview i.anticon-control',
    photo: '#ddReview div[class*="CommonWidget_reviewImgs__"]',
    verified: '#ddReview div[class*="ListLayout_verify__"]'
  };
  const verificationNote = 'Verification status supplied by review provider; original sales channel not independently confirmed.';

  class FadlashProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.frame = null;
      this.observer = new MutationObserver(() => this.scheduleEnhancement());
      this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        const photo = event.target.closest('[data-fpr-native-control="photo"]');
        if (photo && this.contains(photo)) this.photoOpener = photo;
      }, { capture: true, signal: this.controller.signal });
      const afterNativeAction = () => {
        if (!this.photoOpener) return;
        const started = performance.now();
        const restore = () => {
          if (!this.controller || !this.photoOpener?.isConnected) return;
          const visible = element => {
            const rect = element.getBoundingClientRect();
            for (let node = element; node; node = node.parentElement) {
              const style = getComputedStyle(node);
              if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
            }
            return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < innerWidth && rect.bottom > 0 && rect.top < innerHeight;
          };
          if ([...document.querySelectorAll('[role="dialog"], dialog[open]')].some(visible)) {
            if (performance.now() - started < 1000) requestAnimationFrame(restore);
            return;
          }
          const active = document.activeElement;
          const formerDialog = active?.closest('[role="dialog"]');
          if (active === document.body || (formerDialog && !visible(formerDialog))) {
            this.photoOpener.focus({ preventScroll: true });
          }
          this.photoOpener = null;
        };
        requestAnimationFrame(restore);
      };
      document.addEventListener('click', afterNativeAction, { capture: true, signal: this.controller.signal });
      document.addEventListener('keydown', event => { if (event.key === 'Escape') afterNativeAction(); }, { capture: true, signal: this.controller.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this || event.target.contains(this)) this.disconnect();
      }, { signal: this.controller.signal });
      this.enhance();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.observer = null;
      this.photoOpener = null;
    }

    scheduleEnhancement() {
      if (!this.controller || this.frame !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.enhance();
      });
    }

    enhanceControl(control, kind, label) {
      // Do not introduce nested interactive controls if the provider changes its markup.
      if (control.closest('button, a[href], input, select, textarea, [role="button"]') && control.getAttribute('data-fpr-native-control') !== kind) return;
      control.setAttribute('data-fpr-native-control', kind);
      control.setAttribute('role', 'button');
      control.setAttribute('tabindex', '0');
      control.setAttribute('aria-label', label);
    }

    enhance() {
      if (!this.controller || !this.observer || !this.isConnected) return;
      // Our labels are presentation changes. Observe only subsequent provider updates.
      this.observer.disconnect();
      try {
        this.querySelectorAll(selectors.expand).forEach((control) => {
          const nativeText = control.textContent.trim();
          if (nativeText === '[...]' || nativeText === '[…]' || nativeText === 'Read full review') {
            this.enhanceControl(control, 'expand', 'Read full review');
            if (control.getAttribute('data-fpr-native-control') === 'expand' && nativeText !== 'Read full review') control.textContent = 'Read full review';
          }
          // The original control disappears after expansion; do not invent a collapse action.
        });
        this.querySelectorAll(selectors.write).forEach((control) => {
          this.enhanceControl(control, 'write', control.textContent.trim() || 'Write a review');
        });
        this.querySelectorAll(selectors.sort).forEach((control) => this.enhanceControl(control, 'sort', 'Sort reviews'));
        this.querySelectorAll(selectors.photo).forEach((control) => {
          if (control.querySelector('a[href], button, input, select, textarea, [role="button"]')) return;
          this.enhanceControl(control, 'photo', 'Open review photo');
        });
        this.querySelectorAll(selectors.verified).forEach((badge) => {
          const existingTitle = badge.getAttribute('title') || '';
          if (!existingTitle.includes(verificationNote)) badge.setAttribute('title', existingTitle ? `${existingTitle} ${verificationNote}` : verificationNote);
        });
      } finally {
        if (this.observer && this.controller) this.observer.observe(this, { childList: true, characterData: true, subtree: true });
      }
    }

    handleKeydown(event) {
      if (event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const control = event.target.closest('[data-fpr-native-control]');
      if (!control || !this.contains(control) || control.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      if (control.getAttribute('data-fpr-native-control') === 'photo') this.photoOpener = control;
      // Reuse the existing UI action; no review API, submission, filter or data operation is added.
      control.click();
    }
  }

  customElements.define('fadlash-product-reviews', FadlashProductReviews);
})();
